import { Trash2 } from 'lucide-react';
import { useDungeonEditorStore } from '@/store/dungeonEditorStore';

export function RoomPanel() {
  const selectedRoomId = useDungeonEditorStore((s) => s.selectedRoomId);
  const mapData = useDungeonEditorStore((s) => s.mapData);
  const updateRoom = useDungeonEditorStore((s) => s.updateRoom);
  const removeRoom = useDungeonEditorStore((s) => s.removeRoom);

  const room = mapData.rooms.find((r) => r.id === selectedRoomId);

  if (!room) return null;

  const roomEntities = mapData.entities.filter((e) => e.roomId === room.id);
  const roomTriggers = mapData.triggers.filter((t) => t.roomId === room.id);

  const labelClass = 'block text-xs text-gray-500 uppercase tracking-wide mb-1';
  const inputClass = 'w-full px-2 py-1.5 bg-gray-950 border border-gray-700 rounded text-sm text-gray-100';

  const purposeOptions = [
    { value: 'entrance', label: 'Entrance' },
    { value: 'combat', label: 'Combat' },
    { value: 'puzzle', label: 'Puzzle' },
    { value: 'treasure', label: 'Treasure' },
    { value: 'boss', label: 'Boss' },
    { value: 'empty', label: 'Empty' },
    { value: 'storage', label: 'Storage' },
    { value: 'shrine', label: 'Shrine' },
    { value: 'trap', label: 'Trap' },
  ];

  const zoneOptions = [
    { value: 0, label: '0 — Outer' },
    { value: 1, label: '1 — Inner' },
    { value: 2, label: '2 — Deep' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-200">Room</h2>
        <button
          onClick={() => removeRoom(room.id)}
          className="p-1 text-gray-500 hover:text-red-400 hover:bg-red-900/20 rounded transition-colors"
          title="Delete room"
        >
          <Trash2 size={14} />
        </button>
      </div>

      <div>
        <label className={labelClass}>Name</label>
        <input
          type="text"
          className={inputClass}
          value={room.name}
          onChange={(e) => updateRoom(room.id, { name: e.target.value })}
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className={labelClass}>Purpose</label>
          <select
            className={inputClass}
            value={room.purpose}
            onChange={(e) => updateRoom(room.id, { purpose: e.target.value })}
          >
            {purposeOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass}>Zone</label>
          <select
            className={inputClass}
            value={room.zone}
            onChange={(e) => updateRoom(room.id, { zone: Number(e.target.value) })}
          >
            {zoneOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className={labelClass}>Description</label>
        <textarea
          className={`${inputClass} resize-none`}
          rows={4}
          placeholder="What does the player see when they enter?"
          value={room.description}
          onChange={(e) => updateRoom(room.id, { description: e.target.value })}
        />
      </div>

      <div>
        <label className={labelClass}>Atmosphere</label>
        <textarea
          className={`${inputClass} resize-none`}
          rows={2}
          placeholder="Sounds, smells, temperature..."
          value={room.atmosphere}
          onChange={(e) => updateRoom(room.id, { atmosphere: e.target.value })}
        />
      </div>

      <div>
        <label className={labelClass}>Secrets</label>
        <textarea
          className={`${inputClass} resize-none`}
          rows={2}
          placeholder="Hidden things revealed by search..."
          value={room.secrets}
          onChange={(e) => updateRoom(room.id, { secrets: e.target.value })}
        />
      </div>

      {roomEntities.length > 0 && (
        <div>
          <label className={labelClass}>Entities in Room</label>
          <div className="space-y-1">
            {roomEntities.map((entity) => (
              <div
                key={entity.id}
                className="flex items-center gap-2 px-2 py-1 bg-gray-950 border border-gray-700 rounded text-xs text-gray-300"
              >
                <span className="text-base leading-none">{entity.icon}</span>
                <span className="flex-1 truncate">{entity.name}</span>
                <span className="text-gray-500 capitalize">{entity.type}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {roomTriggers.length > 0 && (
        <div>
          <label className={labelClass}>Triggers in Room</label>
          <div className="space-y-1">
            {roomTriggers.map((trigger) => (
              <div
                key={trigger.id}
                className="flex items-center gap-2 px-2 py-1 bg-gray-950 border border-gray-700 rounded text-xs text-gray-300"
              >
                <span>⚡</span>
                <span className="flex-1 truncate">{trigger.name}</span>
                <span className="text-gray-500 capitalize">{trigger.condition}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <label className={labelClass}>Position</label>
        <div className="text-xs text-gray-400 bg-gray-950 border border-gray-700 rounded px-2 py-1.5">
          ({room.x}, {room.y}) — {room.width} x {room.height}
        </div>
      </div>
    </div>
  );
}
